import { Row, Col, Button, Form, Upload, message, Input, Divider } from 'antd';
import { CheckOutlined, UploadOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import DF from 'DisplayFramework';
import HeroTitle from 'components/theme_components/HeroTitle';
import StatCard from 'components/theme_components/StatCard';
import ButtonLink from 'components/theme_components/ButtonLink';
import Notifications from 'components/theme_components/Notifications';
import { importBonification } from 'services/parametersService';
import BonificationStats from './BonificationStats';

interface BonusData {
  [key: string]: any;
}

export type BonusProps = {
  color: string;
};

declare global {
  interface Window {
    readXlsxFile: any;
  }
}

const Bonus = (props: BonusProps) => {
  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState<BonusData[]>([]);
  const [fileName, setFileName] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [excelReady, setExcelReady] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // Load the Excel reading library from CDN
  useEffect(() => {
    const scriptId = "read-excel-file-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://cdn.jsdelivr.net/npm/read-excel-file@5.6.1/bundle/read-excel-file.min.js";
      script.onload = () => {
        console.log("read-excel-file loaded");
        setExcelReady(true);
      };
      script.onerror = () => {
        message.error("Failed to load Excel reading library.");
      };
      document.body.appendChild(script);
    } else {
      setExcelReady(true);
    }
  }, []);

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const handleUpload = async (file: File) => {
    setFileName('');
    setDataSource([]);
    setUploadedFile(null);

    if (!excelReady || !window.readXlsxFile) {
      message.error("Excel reading library not ready yet. Please try again shortly.");
      return false;
    }

    const allowedTypes = ['.xlsx', '.xls'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!allowedTypes.includes(fileExtension)) {
      message.error('Seuls les fichiers Excel (.xlsx, .xls) sont acceptés.');
      return false;
    }

    const expectedHeaders = ['nom STM', 'nom TM', 'RRDI', 'Activite', 'taux', 'date de debut', 'date de fin'];

    try {
      const rows = await window.readXlsxFile(file);

      if (rows.length === 0) {
        message.error('Le fichier Excel est vide.');
        return false;
      }

      const headers = rows[0] as string[];
      const normalizedHeaders = headers.map(h => String(h).trim().toLowerCase());
      const normalizedExpectedHeaders = expectedHeaders.map(h => h.toLowerCase());

      const missingHeaders = normalizedExpectedHeaders.filter(expectedHeader =>
        !normalizedHeaders.includes(expectedHeader)
      );

      if (missingHeaders.length > 0) {
        message.error('Le fichier Excel est invalide.');
        return false;
      }

      const extraHeaders = normalizedHeaders.filter(header =>
        !normalizedExpectedHeaders.includes(header) && header.trim() !== ''
      );

      if (extraHeaders.length > 0) {
        console.warn('En-têtes supplémentaires trouvées :', extraHeaders);
      }

      // Convertir les données en objets
      const excelDataAsJson = rows.slice(1).map((row: any[], index: any) => {
        const obj: BonusData = {};
        headers.forEach((header, headerIndex) => {
          const value = row[headerIndex];
          obj[String(header)] = value !== null && value !== undefined ? String(value) : '';
        });
        return obj;
      }).filter((row: { [s: string]: unknown; } | ArrayLike<unknown>) => {
        return Object.values(row).some(val => val !== '');
      });

      if (excelDataAsJson.length === 0) {
        message.error('Le fichier ne contient aucune donnée valide.');
        return false;
      }

      setDataSource(excelDataAsJson);
      setFileName(file.name);
      setUploadedFile(file);

      message.success(`Fichier "${file.name}" chargé avec succès. ${excelDataAsJson.length} lignes détectées.`);
    } catch (error) {
      console.error('Erreur de lecture Excel :', error);
      message.error('Le fichier Excel est invalide ou corrompu. Vérifiez que c\'est bien un fichier Excel valide.');
    }

    return false;
  };

  const handleSave = async () => {
    if (!uploadedFile) {
      Notifications({
        status: 'error',
        message: 'Votre fichier n\'est pas valide',
        description: 'Aucune bonification n\'a été ajoutée !',
        duration: 2,
        placement: 'top',
      });
      return;
    }

    if (dataSource.length === 0) {
      Notifications({
        status: 'error',
        message: 'Fichier vide',
        description: 'Le fichier ne contient aucune donnée à importer.',
        duration: 2,
        placement: 'top',
      });
      return;
    }

    try {
      const response = await importBonification(uploadedFile);


      if (response.code === '200') {
        Notifications({
          status: 'success',
          message: response.message,
          //description: `${dataSource.length} bonifications ont été détectées.`,
          description: response.description,
          duration: 2,
          placement: 'top',
        });
        setRefreshKey(prev => prev + 1);

      } else {
        Notifications({
          status: 'error',
          message: response.message,
          description: response.description,
          duration: 2,
          placement: 'top',
        });

      }

      // Reset des champs
      setUploadedFile(null);
      setFileName('');
      setDataSource([]);
      form.resetFields();

    } catch (error) {
      console.error('Erreur API:', error);
      Notifications({
        status: 'error',
        message: 'Échec de l\'import',
        description: error instanceof Error ? error.message : 'Une erreur est survenue lors de l\'import des bonifications.',
        duration: 4,
        placement: 'top',
      });
    }
  };

  return (
    <DF.Provider pagcode="productlist">
      <div className="bg-color-gray">
        <HeroTitle
          form={form}
          color={props.color}
          title="Bonus"
          showFilter={false}
          display
          className="productlist"
          noTitleOverlay
        />
        <Form form={form}>
          <div className="container">
            <Row className="mb-4 ut-wizard-form" gutter={24} justify="center" align="middle">
              <Col span={22} className="globalvision">
                <StatCard
                  title=""
                  value="Vous pouvez modifier en masse les bonifications. Pour cela, téléchargez au préalable le fichier template (disponible ici), complétez-le dans un logiciel tableur, puis uploadez-le sur cette page. Une vérification vous confirmera si le fichier est valide et si vos données ont bien été prises en compte."
                  variant="secondary"
                  className="mb-8 staticNotf mt-8 blue"
                  valueStyle={{
                    backgroundColor: '#435FCB14',
                  }}
                />
                <Form.Item
                  name="upload"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                  extra=""
                >
                  <Row gutter={16} align="middle" justify="space-between">
                    <Col span={20}>
                      <Row>
                        <Col>
                          <Upload
                            accept=".xlsx,.xls"
                            showUploadList={false}
                            beforeUpload={handleUpload}
                            maxCount={1}
                          >
                            <ButtonLink
                              icon={<UploadOutlined color="orange" />}
                              style={{ width: '150px' }}
                              comcode={'upload'}
                              className="ut-reset-btn ut-btn-bordred"
                            />
                          </Upload>
                        </Col>
                        <Col span={19}>
                          <Input
                            style={{ marginLeft: '-25px', width: '100%' }}
                            value={fileName}
                            readOnly
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col>
                      <ButtonLink
                        icon={<CheckOutlined />}
                        style={{ width: '150px' }}
                        comcode={'save'}
                        type="primary"
                        className="ut-reset-btn"
                        onClick={handleSave}
                      />
                    </Col>
                  </Row>
                </Form.Item>

                {/* Add Statistics Section */}
                <Divider orientation="left" style={{ marginTop: '40px', marginBottom: '24px' }}>
                </Divider>

                <BonificationStats key={refreshKey} color={props.color} />
              </Col>
            </Row>
          </div>
        </Form>
      </div>
    </DF.Provider>
  );
};

export default Bonus;